// Handles all Stripe payment interactions.
// Components call redirectToCheckout() or redirectToPortal() and this
// hook handles the API call, error state, and redirect.

import { useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { API_BASE } from '../lib/constants';
import type { TierKey } from '../types';

type Billing = 'monthly' | 'annual';

interface UseStripeResult {
  loading: boolean;
  error: string | null;
  redirectToCheckout: (tier: TierKey, billing: Billing) => Promise<void>;
  redirectToPortal: () => Promise<void>;
}

export function useStripe(): UseStripeResult {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectToCheckout = useCallback(async (tier: TierKey, billing: Billing) => {
    if (!user) {
      setError('You must be signed in to upgrade.');
      return;
    }

    if (tier === 'free') return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_email: user.primaryEmailAddress?.emailAddress || '',
          tier,
          billing,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
    // Note: don't setLoading(false) on success — page is redirecting
  }, [user]);

  const redirectToPortal = useCallback(async () => {
    if (!user) {
      setError('You must be signed in to manage your subscription.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/stripe/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to open billing portal');
      }

      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
  }, [user]);

  return { loading, error, redirectToCheckout, redirectToPortal };
}